import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import { DebouncedFunc, cloneDeep, debounce, isEqual } from 'lodash';
import EventBus from '@vertical-plus/vue-js-core/dist/util/event/event-bus';
import FormFieldValuesInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-field-values-interface';
import FormInterface from '@vertical-plus/vue-js-components/lib/model/vos/form/form-interface';
import Job from '@/model/entity/job/job';
import JobContribution from '../job-contribution/index.vue';
import JobContributionEntity from '@/model/entity/job/job-contribution';
import JobContributions from '../job-contributions/index.vue';
import JobService from '@/model/service/job/job-service';
import JobTasks from '../job-tasks/index.vue';

@Component({
    components: {
        'app-job-contribution': JobContribution,
        'app-job-contributions': JobContributions,
        'app-job-tasks': JobTasks,
    },
})
export default class JobInformation extends Vue
{
    @Prop()
    private value!: Job;

    private job: Job|null = null;

    private initialContribution: JobContributionEntity|null = null;

    private form: FormInterface|null = null;
    private formTags: FormInterface|null = null;

    private currentTags: string[] = [];
    private debouncedUpdateTags!: DebouncedFunc<() => Promise<void>>;

    private contributionDocked = false;

    private created()
    {
        this.debouncedUpdateTags = debounce(
            this.updateTags, 4000, { maxWait: 15000 },
        );

        this.onJobChanged();
    }

    @Watch('value')
    private onJobChanged()
    {
        if (!this.value)
        {
            this.job = null;
            this.initialContribution = null;
            this.form = null;

            return;
        }

        this.job = new Job(cloneDeep(this.value.dto));

        this.initialContribution = new JobContributionEntity({
            id: null,
            job: this.job.dto,
            clientUser: this.job.creatorId && parseInt(this.job.creatorId) > 0 ? null : this.job.dto.clientUser,
            user: this.job.dto.creator,
            notes: this.job.description ?? '',
            attachment1: null,
            clientVisible: true,
            markdown: true,
            amount: '0.00',
            timeSpent: 0,
            timesheet: null,
            type: null,
            deleted: false,
            dateCreated: this.job.dto.dateCreated,
        });

        this.generateQuickEditForm();
        this.generateTagsForm();
    }

    /**
     * Generates the quick edit form
     *
     * @returns
     */

    private generateQuickEditForm()
    {
        if (!this.job)
        {
            this.form = null;

            return;
        }

        const form: FormInterface =
        {
            items: [
                {
                    key: 'clientUser',
                    type: 'integer',
                    title: this.$t('general.field.client_user').toString(),
                    value: this.job.clientUserId,
                    required: false,
                    clearable: true,
                    optionsCallback: async(search: string) => this.$serviceContainer.clientService.clientUserFormOptionsCallback(search, this.job ? this.job.clientId : null),
                    titlesCallback: async(values: string[]) => this.$serviceContainer.clientService.clientUserFormTitlesCallback(values, this.job ? this.job.clientId : null),
                },
                {
                    key: 'ticket',
                    type: 'string',
                    title: this.$t('job.field.ticket').toString(),
                    value: this.job.ticket,
                    required: false,
                    options: JobService.ticketOptions,
                    dropdown: true,
                },
                {
                    key: 'stage',
                    type: 'integer',
                    title: this.$t('job.field.stage').toString(),
                    value: this.job.stageId,
                    required: true,
                    optionsCallback: async(search: string) => this.$serviceContainer.jobService.jobStageFormOptionsCallback(search),
                    titlesCallback: async(values: string[]) => this.$serviceContainer.jobService.jobStageFormTitlesCallback(values),
                },
                {
                    key: 'assignee',
                    type: 'string',
                    title: this.$t('job.field.assigned_to').toString(),
                    value: this.job.assigneeId,
                    required: false,
                    clearable: true,
                    optionsCallback: async(search: string) => this.$serviceContainer.userService.formOptionsCallback(search),
                    titlesCallback: async(values: string[]) => this.$serviceContainer.userService.formTitlesCallback(values),
                },
            ],
        };

        this.form = Object.assign({}, form);
    }

    /**
     * Generates the tags form
     *
     * @returns
     */

    private generateTagsForm()
    {
        if (!this.job)
        {
            this.formTags = null;

            return;
        }

        const form: FormInterface =
        {
            items: [
                {
                    key: 'tags',
                    type: 'string',
                    title: this.$t('general.field.tags').toString(),
                    value: this.job.tags,
                    required: false,
                    multiple: true,
                    optionsCallback: async(search: string) => this.$serviceContainer.tagService.tagFormOptionsCallback(search),
                    titlesCallback: async(values: string[]) => this.$serviceContainer.tagService.tagFormTitlesCallback(values),
                    allowNewOptions: true,
                },
            ],
        };

        this.formTags = Object.assign({}, form);
    }

    /**
     * Handles quick edit value changes
     */

    private async onQuickEditValuesChanged(values: FormFieldValuesInterface)
    {
        if (!this.job)
        {
            return;
        }

        let changed = false;

        // Client User
        if (values.clientUser !== this.job.clientUserId)
        {
            this.job.clientUserId = values.clientUser as number;
            changed = true;
        }

        // Ticket
        if (values.ticket !== this.job.ticket)
        {
            this.job.ticket = values.ticket as string;
            changed = true;
        }

        // Stage
        if (values.stage && values.stage !== this.job.stageId)
        {
            this.job.stageId = values.stage as number;
            changed = true;
        }

        // Assignee
        if (values.assignee !== this.job.assigneeId)
        {
            this.job.assigneeId = values.assignee as string;
            changed = true;
        }

        if (changed)
        {
            try
            {
                const updated = await this.$serviceContainer.jobService.updateJob(this.job);

                EventBus.instance.$emit(
                    'notification',
                    this.$t('general.message.saved_successfully'),
                    'success',
                );

                this.$emit('input', updated);
            }
            catch (error: unknown)
            {
                EventBus.instance.$emit(
                    'notification',
                    this.$t('general.error.failed_saving_server_error'),
                    'error',
                );
            }
        }
    }

    /**
     * Handles quick edit value changes
     */

    private async onTagsChanged(values: FormFieldValuesInterface)
    {
        if (!this.job)
        {
            return;
        }

        this.currentTags = values.tags as string[];
        this.debouncedUpdateTags();
    }

    /**
     * Updates the tags
     *
     * @returns
     */

    private async updateTags()
    {
        if (!this.job || isEqual(this.job.tags, this.currentTags))
        {
            return;
        }

        try
        {
            this.job.tags = this.currentTags;

            const updated = await this.$serviceContainer.jobService.updateJob(this.job);

            EventBus.instance.$emit(
                'notification',
                this.$t('general.message.saved_successfully'),
                'success',
            );

            this.$emit('input', updated);
        }
        catch (error: unknown)
        {
            EventBus.instance.$emit(
                'notification',
                this.$t('general.error.failed_saving_server_error'),
                'error',
            );
        }
    }

    /**
     * Handles contribution docking
     *
     * @param docked
     */

    private onToggleDocked(docked: boolean)
    {
        this.contributionDocked = docked;
    }
}